<script setup lang="ts">
/**
 * The pick-one selector of every dialog that starts with "which kind": connector types,
 * dataset types. A grid of Choice rows, so the look of every such selector is decided in
 * one place. `columns` is how many sit in a row when there is room for them (two by
 * default, any count allowed) — a row never squeezes a title past reading, so on a narrow
 * dialog the grid drops to fewer columns on its own. `height` bounds the box and scrolls
 * inside it, for a list long enough to outgrow the dialog; without it the grid is
 * content-height. An option the contract names but does not accept yet is passed `disabled`.
 */
import { computed } from 'vue'
import Choice from './Choice.vue'
import Icon from './Icon.vue'
import type { IconName } from '~/utils/icons'

export interface ChoiceOption { id: string; title: string; facts?: string; icon?: IconName; disabled?: boolean }
const props = withDefaults(defineProps<{ options: ChoiceOption[]; modelValue: string; columns?: number; height?: string }>(), { columns: 2, height: '' })
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
/* The asked share of the row, but never narrower than one row needs to read: auto-fit then
   lays out exactly `columns` tracks when the box is wide enough and fewer when it is not. */
const track = computed(() => {
  const cap = Math.max(1, Math.round(props.columns))
  const share = `calc((100% - ${cap - 1} * var(--choice-gap, 0.375rem)) / ${cap})`
  return {
    gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, max(var(--choice-min-width, 11.428571rem), ${share})), 1fr))`,
    gap: 'var(--choice-gap, 0.375rem)',
    ...(props.height ? { height: props.height } : {}),
  }
})
</script>

<template>
  <div class="grid shrink-0 overflow-y-auto" :class="height && 'pr-0.5'" :style="track">
    <Choice
      v-for="option in options" :key="option.id" :title="option.title" :facts="option.facts"
      :selected="modelValue === option.id" :disabled="option.disabled" @click="emit('update:modelValue', option.id)">
      <Icon v-if="option.icon" :name="option.icon" :size="13" class="ml-auto shrink-0 text-muted-foreground" />
    </Choice>
  </div>
</template>
